import type { ConditionId } from "../types/entity.js";

/**
 * Flat stat hooks attached to each condition (B04).
 *
 * B03 owns the condition *lifecycle* (apply/tick/cleanse) and deliberately
 * leaves modifier numbers out of `ConditionDef` so the lifecycle can evolve
 * independently of the math. This module is the small companion table:
 * "when a condition is active, what flat bonuses and penalties come with it?"
 *
 * Only the subset of v1 conditions that touch the attack/damage pipeline
 * appear here. Non-combat hooks (Charm gating UI input, Silence blocking
 * spellcast, Sleep blocking actions, Slow scaling cooldowns) live with
 * their consuming systems.
 *
 * Convention: positive numbers favour the holder; negative numbers hurt them.
 *   - `toHitSelf`: added to the attacker's own attack roll when this is one
 *     of *their* conditions.
 *   - `toHitFromAttackers`: added to every attacker's roll *against* this
 *     entity.
 *   - `damageBonus`: flat bonus to damage rolls the holder makes.
 *
 * Conditions absent from the registry contribute zero.
 */
export interface ConditionStatHooks {
  readonly toHitSelf?: number;
  readonly toHitFromAttackers?: number;
  readonly damageBonus?: number;
}

export const CONDITION_STAT_HOOKS: Readonly<Partial<Record<ConditionId, ConditionStatHooks>>> = {
  // Invisible attacker has advantage in 5e; the v1 replacement is a flat +5.
  // Attackers against an invisible target have disadvantage → −5 to hit.
  invisible: { toHitSelf: +5, toHitFromAttackers: -5 },
  // Blinded attacker has disadvantage (−5); attackers against a blinded
  // target have advantage (+5).
  blinded: { toHitSelf: -5, toHitFromAttackers: +5 },
  // bkcrawl's Strong buff: +2 to hit and damage (mirrors +2 STR mod).
  strong: { toHitSelf: +2, damageBonus: +2 },
  // Poisoned in 5e imposes disadvantage on attack rolls → −5 in v1.
  poisoned: { toHitSelf: -5 },
  // Prone attacker has disadvantage at range / advantage in melee; v1
  // collapses to a flat −5 to simplify.
  prone: { toHitSelf: -5, toHitFromAttackers: +5 },
  // Restrained: attacker disadvantage, attackers advantage.
  restrained: { toHitSelf: -5, toHitFromAttackers: +5 },
  // Frightened attacker has disadvantage while source is in sight; v1
  // approximates with a flat −5.
  frightened: { toHitSelf: -5 },
};

/**
 * Sum the `toHitSelf` hook across every active condition on the attacker.
 * Missing conditions contribute 0. Multiple hooks stack additively.
 */
export function sumToHitSelf(conditions: Iterable<ConditionId>): number {
  let total = 0;
  for (const id of conditions) {
    total += CONDITION_STAT_HOOKS[id]?.toHitSelf ?? 0;
  }
  return total;
}

/**
 * Sum the `toHitFromAttackers` hook across every active condition on the
 * defender. This is what attackers *gain* by targeting someone with these
 * conditions (e.g. Blinded defender → +5 for every attacker).
 */
export function sumToHitFromAttackers(conditions: Iterable<ConditionId>): number {
  let total = 0;
  for (const id of conditions) {
    total += CONDITION_STAT_HOOKS[id]?.toHitFromAttackers ?? 0;
  }
  return total;
}

/** Sum the `damageBonus` hook across every active condition on the attacker. */
export function sumDamageBonus(conditions: Iterable<ConditionId>): number {
  let total = 0;
  for (const id of conditions) {
    total += CONDITION_STAT_HOOKS[id]?.damageBonus ?? 0;
  }
  return total;
}

export interface ConditionModifierSources {
  /** Active conditions on the attacker. */
  readonly attacker?: Iterable<ConditionId>;
  /** Active conditions on the defender. */
  readonly defender?: Iterable<ConditionId>;
}

/**
 * Convenience aggregate: fold both sides of an attack into the flat
 * `hitBonus` and `damageBonus` numbers the resolvers already accept.
 *
 * Returned shape is deliberately compatible with `AttackInput.hitBonus`
 * and `DamageInput.modifier` addend fields — callers can just
 * `attackInput.hitBonus = weaponHitBonus + mods.hitBonus`.
 */
export function aggregateAttackModifiers(
  sources: ConditionModifierSources,
): { readonly hitBonus: number; readonly damageBonus: number } {
  const attacker = sources.attacker ?? [];
  const defender = sources.defender ?? [];
  const hitBonus = sumToHitSelf(attacker) + sumToHitFromAttackers(defender);
  const damageBonus = sumDamageBonus(attacker);
  return { hitBonus, damageBonus };
}
